import React, { FC, memo } from "react"
import { Form, Input, Button, Space } from "antd"
// @ts-ignore
import { Formik, FormikActions } from "formik"
import { createInputHelpers } from "../../../../helpers/createInputHelpers"
import LayoutForm from "../../../../views/LayoutForm/LayoutForm"
import { isDefined } from "../../../../helpers/typeScriptHelpers"
import { BookerPaymentMethodInput } from "../../../../generated/graphql"
import { v4 as uuidv4 } from "uuid"
import { colorBackgroundBase } from "../../../../styles/colors"
import { margin, radiusS } from "../../../../styles/layout"
import { SepaDebitValidationSchema } from "./SepaDebitValidationSchema"
import { red } from "@ant-design/colors"
import LoadingContent from "../../../../views/LoadingContent/LoadingContent"

export type ModalDataType = Pick<BookerPaymentMethodInput, "sepaAccountholder" | "iban" | "bic" | "bank" | "id">

type SepaDebitPaymentMethodModalProps = {
    initialValues?: ModalDataType
    onSubmit?: (values: ModalDataType, formikActions: FormikActions<ModalDataType>) => void
    onCancel?: () => void
    updatingBooker?: boolean
}

const defaultInitialValues: ModalDataType = {
    sepaAccountholder: "",
    iban: "",
    bic: "",
    bank: "",
    id: uuidv4(),
}

const SepaDebitPaymentMethodModal: FC<SepaDebitPaymentMethodModalProps> = ({
    initialValues = defaultInitialValues,
    onSubmit = () => undefined,
    onCancel = () => undefined,
    updatingBooker = false,
}) => {
    type SubmitButtonProps = {
        handleSubmit: () => void
        style: React.CSSProperties
        disabled?: boolean
        danger?: boolean
    }

    const Submitbutton: FC<SubmitButtonProps> = ({ handleSubmit, style, disabled, danger }) => {
        return (
            <Button
                type='primary'
                block
                onClick={handleSubmit}
                style={style}
                disabled={disabled === true}
                danger={danger === true}
            >
                Lastschriftdaten Speichern
            </Button>
        )
    }

    if (updatingBooker) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Space size='middle' style={{ margin: -3 }}>
                    <LoadingContent style={{ width: "20px", height: "20px" }} size='small' />
                    <div>Bezahlmethode wird gespeichert</div>
                </Space>
            </div>
        )
    }

    return (
        <Formik<ModalDataType>
            validationSchema={SepaDebitValidationSchema}
            initialValues={initialValues}
            enableReinitialize={false}
            onSubmit={onSubmit}
        >
            {(formikProps) => {
                const { values, handleSubmit } = formikProps
                const { handleBlur, handleTextInputChange, handleIbanInputChange } = createInputHelpers(formikProps)
                const ibanError = formikProps.errors.iban && formikProps.touched.iban
                const sepaAccountholderError =
                    formikProps.errors.sepaAccountholder && formikProps.touched.sepaAccountholder
                const dataComplete = Boolean(
                    values.sepaAccountholder && values.iban && values.sepaAccountholder !== "" && values.iban !== ""
                )
                const formIsValid = Boolean(formikProps.isValid)

                return (
                    <LayoutForm
                        columns={2}
                        style={{
                            background: colorBackgroundBase,
                            border: "1px solid lightgrey",
                            borderRadius: radiusS,
                            paddingBottom: margin.xs,
                        }}
                    >
                        <Input type='hidden' name='id' value={values.id} />
                        <Form.Item label='Kontoinhaber' style={{ gridColumn: "span 2" }} required>
                            <Input
                                data-testid='SepaDebitPaymentMethodModalAccountHolder'
                                onChange={handleTextInputChange("sepaAccountholder")}
                                onBlur={handleBlur("sepaAccountholder")}
                                value={isDefined(values.sepaAccountholder) ? values.sepaAccountholder : ""}
                            />
                        </Form.Item>
                        <div
                            style={{
                                opacity: sepaAccountholderError ? 1 : 0,
                                gridColumn: "span 2",
                                fontSize: "0.9em",
                                padding: "0.5em",
                                paddingBottom: 0,
                                paddingTop: 0,
                                marginTop: -10,
                                color: red[4],
                            }}
                        >
                            Bitte gib Vor- und Nachname des Kontoinhabers an.
                        </div>
                        <Form.Item label='IBAN' style={{ gridColumn: "span 2" }} required>
                            <Input
                                data-testid='SepaDebitPaymentMethodModalIban'
                                onChange={handleIbanInputChange("iban")}
                                onBlur={handleBlur("iban")}
                                value={isDefined(values.iban) ? values.iban : ""}
                            />
                        </Form.Item>
                        <div
                            style={{
                                opacity: ibanError ? 1 : 0,
                                gridColumn: "span 2",
                                fontSize: "0.9em",
                                padding: "0.5em",
                                paddingBottom: 0,
                                paddingTop: 0,
                                marginTop: -10,
                                color: red[4],
                            }}
                        >
                            <div>Bitte gib eine gültige IBAN ein.</div>
                            <div>Bsp.: "DE 11 5205 1373 5120 7101 31,</div>
                            <div>AT 61 1904 30023457 3201"</div>
                        </div>
                        <Space direction='vertical' size='small' style={{ gridColumn: "span 2" }}>
                            <Submitbutton
                                handleSubmit={handleSubmit}
                                style={{
                                    gridColumn: "span 2",
                                    color: !formIsValid || !dataComplete ? red[3] : undefined,
                                    borderColor: !formIsValid || !dataComplete ? red[3] : undefined,
                                }}
                                disabled={!formIsValid || !dataComplete}
                                danger={!formIsValid || !dataComplete}
                            />
                        </Space>
                    </LayoutForm>
                )
            }}
        </Formik>
    )
}

export default memo(SepaDebitPaymentMethodModal)
